/**
 * «Команда» — сотрудники, должности и права, заявки (§2 ТЗ портала, этап P2).
 *
 * Переносит веб-экран «Пользователи и доступ» (§13.3 ТЗ платформы) и
 * «Доступ к деньгам» операционного бота в один раздел:
 *
 * - `/team` — сотрудники поиском и карточка человека `/team/people/:id`:
 *   должность, вход в агенты, флаг денег, личные исключения прав;
 * - `/team/positions` — права должностей и новая должность;
 * - `/team/requests` — заявки на доступ и как пригласить.
 *
 * Раздел видит владелец, а **с 18.09.2026 и управляющий — на просмотр**
 * (§2 ТЗ портала, просьба владельца): навигация несёт признак
 * `can_view_team`, адреса-чтения `access.py` открыты
 * `require_team_view_web`, адреса-действия остались владельцу. Здесь
 * разница видна одним переключателем — контекстом `MayEditTeam`.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Denied } from '@shell/states';
import type { Navigation } from '../../api';
import { Link, useRoute } from '../../router';
import { MayEditTeam, VIEW_ONLY_TEXT } from './access';
import { TEAM_KEYS, teamApi } from './api';
import type { UsersScreen } from './api';
import { People } from './People';
import { Positions } from './Positions';
import { Requests } from './Requests';
import { Waiters } from './Waiters';
import './team.css';

type Tab = 'people' | 'positions' | 'waiters' | 'requests';

function tabOf(path: string): Tab {
  // `/team/staff/:id` — карточка сотрудника без Telegram, вкладка «Сотрудники».
  if (path.startsWith('/team/positions')) return 'positions';
  if (path.startsWith('/team/waiters')) return 'waiters';
  if (path.startsWith('/team/requests')) return 'requests';
  return 'people';
}

export default function Team({ nav }: { nav: Navigation }) {
  const route = useRoute();
  const users = useQuery<UsersScreen, ApiError>({
    queryKey: TEAM_KEYS.users,
    queryFn: () => teamApi.users(),
    enabled: nav.user.can_view_team,
    refetchOnWindowFocus: true,
  });
  if (!nav.user.can_view_team) return <Denied />;

  const mayEdit = nav.user.is_host;

  const tab = tabOf(route.path);
  const match = /^\/team\/people\/(\d+)$/.exec(route.path);
  const openId = match ? Number(match[1]) : null;
  const staffMatch = /^\/team\/staff\/(\d+)$/.exec(route.path);
  const openStaffId = staffMatch ? Number(staffMatch[1]) : null;
  const pending = users.data?.requests.length ?? 0;

  const tabs: { code: Tab; title: string; to: string; count?: number }[] = [
    { code: 'people', title: 'Сотрудники', to: '/team' },
    { code: 'positions', title: 'Должности и права', to: '/team/positions' },
    { code: 'waiters', title: 'Официанты кассы', to: '/team/waiters' },
    { code: 'requests', title: 'Заявки', to: '/team/requests', count: pending },
  ];

  return (
    <MayEditTeam.Provider value={mayEdit}>
    <div className={`page team${openId !== null || openStaffId !== null ? ' team--open' : ''}`}>
      <header className="page__head team__head">
        <p className="page__eyebrow">Люди</p>
        <h1 className="page__title">Команда</h1>
      </header>
      <nav className="chips team__tabs" aria-label="Разделы команды">
        {tabs.map((item) => (
          <Link
            key={item.code}
            to={item.to}
            className={`chip${tab === item.code ? ' chip--active' : ''}`}
            aria-current={tab === item.code ? 'page' : undefined}
          >
            {item.title}
            {item.count ? <span className="chip__count">{item.count}</span> : null}
          </Link>
        ))}
      </nav>
      {!mayEdit && <p className="team-viewonly">{VIEW_ONLY_TEXT}</p>}
      {tab === 'people' && (
        <People users={users} openId={openId} openStaffId={openStaffId} meId={nav.user.id} />
      )}
      {tab === 'positions' && <Positions />}
      {tab === 'waiters' && <Waiters />}
      {tab === 'requests' && <Requests users={users} bots={nav.bots} />}
    </div>
    </MayEditTeam.Provider>
  );
}
